import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Download, Zap, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card, SectionHeader, Btn, StatusBadge, Badge, Modal, Field, Input, Select, ModalRow, EmptyState, Table, TR, TD } from '../components/UI';
import { today, fmtDate, fmtAmt, getDueDateInMonth, nowMonth, getInvoiceNumber, isDueTodayInMonth, calcInvoiceDueDate, fetchUsdRate, fetchRatesPKR, toPKR, CURRENCIES } from '../utils/helpers';
import { generateInvoicePDF } from '../utils/pdf';
import { openInvoiceDocument } from '../utils/invoiceHtml';
import { CLIENT_AD_TYPES } from '../utils/storage';
import { celebrate } from '../utils/celebrate';

const EMPTY_CLIENT = { name: '', email: '', billingDay: '', billingType: 'fixed', amount: '', commissionRate: '', adType: '', socialMedia: false, currency: 'PKR' };

export default function InvoicesPage({ store, persist, showToast }) {
  const { invoices = [] } = store;
  const clients       = (store.clients || []).filter(c => c.active !== false && !c.formerClient);
  const formerClients = (store.clients || []).filter(c => c.active === false || c.formerClient);
  const allClients    = store.clients || [];
  const [filter, setFilter] = useState('all');
  const [cur, setCur] = useState('PKR');
  const [usdRate, setUsdRate] = useState(280);
  const [rates, setRates] = useState(null);
  useEffect(() => { fetchUsdRate().then(r => { if (r) setUsdRate(r); }).catch(()=>{}); }, []);
  useEffect(() => { fetchRatesPKR().then(setRates).catch(()=>{}); }, []);

  // Convert a PKR amount for display in the chosen currency
  const showAmt = (pkr) => {
    if (cur === 'USD') return '$' + (Number(pkr||0) / usdRate).toLocaleString('en-US', { maximumFractionDigits: 0 });
    return fmtAmt(pkr);
  };

  // Aging report — outstanding (unpaid) invoices bucketed by days overdue
  const aging = useMemo(() => {
    const buckets = { d30: 0, d60: 0, d90: 0 };
    let totalOutstanding = 0, overdue = 0;
    const t = new Date(today());
    const byCurrency = {};
    (invoices || []).filter(i => i.status !== 'paid' && Number(i.amount||0) > 0).forEach(i => {
      const curr = i.currency || 'PKR';
      byCurrency[curr] = (byCurrency[curr] || 0) + Number(i.amount || 0);
      const amt = toPKR(i.amount, curr, rates);
      totalOutstanding += amt;
      const due = i.dueDate ? new Date(i.dueDate) : null;
      const daysOver = due ? Math.floor((t - due) / 86400000) : 0;
      if (daysOver > 0) overdue += amt;
      if (daysOver >= 90) buckets.d90 += amt;
      else if (daysOver >= 60) buckets.d60 += amt;
      else if (daysOver >= 30) buckets.d30 += amt;
    });
    return { buckets, totalOutstanding, overdue, byCurrency };
  }, [invoices, rates]);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [pendingClientId, setPendingClientId] = useState(null);
  const [salesInput, setSalesInput] = useState('');
  const [showPayModal,  setShowPayModal]  = useState(false);
  const [payClientId,   setPayClientId]   = useState(null);
  const [payAmount,     setPayAmount]     = useState('');

  // Clients due today (no invoice generated this month yet)
  const dueToday = clients.filter(c => {
    if (c.active === false) return false;          // paused — skip billing reminders
    if (!isDueTodayInMonth(c.billingDay)) return false;
    const ym = nowMonth();
    return !invoices.some(i => i.clientId === c.id && i.monthYear === ym);
  });

  const filteredInvoices = (filter === 'all' ? invoices : invoices.filter(i => i.status === filter))
    .slice().sort((a, b) => (b.generatedDate || '').localeCompare(a.generatedDate || ''))
    .map(inv => ({ ...inv, client: allClients.find(c => c.id === inv.clientId) }));

  // --- Client CRUD ---
  function openAddClient() { setEditingClient(null); setClientForm(EMPTY_CLIENT); setShowClientModal(true); }
  function openEditClient(c) { setEditingClient(c); setClientForm({ name: c.name, email: c.email || '', billingDay: c.billingDay, billingType: c.billingType || 'fixed', amount: c.amount || '', commissionRate: c.commissionRate || '', adType: c.adType || '', socialMedia: c.socialMedia || false, currency: c.currency || 'PKR' }); setShowClientModal(true); }

  function saveClient() {
    const { name, email, billingDay, billingType, amount, commissionRate, currency } = clientForm;
    if (!name || !billingDay) return showToast('Name and billing day are required.', 'error');
    if (billingType === 'fixed' && !amount) return showToast('Amount is required for fixed billing.', 'error');
    if (billingType === 'commission' && !commissionRate) return showToast('Commission rate is required.', 'error');
    const updated = { ...store, clients: [...(store.clients || [])] };
    if (editingClient) {
      updated.clients = updated.clients.map(c => c.id === editingClient.id
        ? { ...c, name, email, billingDay: Number(billingDay), billingType, amount: billingType === 'fixed' ? Number(amount) : c.amount, commissionRate: billingType === 'commission' ? Number(commissionRate) : null, adType: clientForm.adType||null, socialMedia: clientForm.socialMedia||false, currency: currency || 'PKR' }
        : c);
      showToast('Client updated.');
    } else {
      updated.clients.push({ id: Date.now().toString(), name, email, billingDay: Number(billingDay), billingType, amount: billingType === 'fixed' ? Number(amount) : null, commissionRate: billingType === 'commission' ? Number(commissionRate) : null, adType: clientForm.adType||null, socialMedia: clientForm.socialMedia||false, currency: currency || 'PKR' });
      showToast('Client added.');
    }
    persist(updated); setShowClientModal(false);
  }

  function deleteClient(id) {
    if (!window.confirm('Delete this client and all their invoices?')) return;
    const removedInvoiceIds = (store.invoices || []).filter(i => i.clientId === id).map(i => i.id);
    const updated = { ...store, clients: store.clients.filter(c => c.id !== id), invoices: store.invoices.filter(i => i.clientId !== id) };
    persist(updated, { deletedIds: { clients: [id], invoices: removedInvoiceIds } }); showToast('Client deleted.');
  }

  function deleteInvoice(invoiceId) {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    const updated = { ...store, invoices: (store.invoices || []).filter(i => i.id !== invoiceId) };
    persist(updated, { deletedIds: { invoices: [invoiceId] } });
    showToast('Invoice deleted.');
  }

  function toggleClientPause(id, currentActive) {
    const nowPausing = currentActive !== false; // if active (or undefined), we are pausing
    const updated = { ...store, clients: (store.clients || []).map(c =>
      c.id === id ? { ...c, active: !nowPausing } : c
    )};
    persist(updated);
    showToast(nowPausing ? 'Client paused — removed from billing reminders.' : 'Client reactivated.');
  }

  // --- Invoice generation ---
  // True if this client already has an invoice for the current month (any status).
  function invoiceExistsThisMonth(clientId) {
    return (store.invoices || []).find(i => i.clientId === clientId && i.monthYear === nowMonth());
  }

  function generateInvoice(clientId) {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    const dupe = invoiceExistsThisMonth(clientId);
    if (dupe) {
      showToast(`An invoice for ${client.name} this month already exists (${dupe.invoiceNumber || dupe.id}). Delete it first if you need to regenerate.`, 'error');
      return;
    }
    if (client.billingType === 'commission') { setPendingClientId(clientId); setSalesInput(''); setShowSalesModal(true); }
    else doGenerate(client, client.amount, null);
  }

  function doGenerate(client, amount, salesFigure) {
    // Safety net: never create a second invoice for the same client + month
    // (covers the commission path if state changed while the sales modal was open).
    if (invoiceExistsThisMonth(client.id)) {
      showToast(`An invoice for ${client.name} this month already exists. Delete it first to regenerate.`, 'error');
      return;
    }
    const updated = { ...store, invoices: [...(store.invoices || [])] };

    // Collect all prior unpaid invoices for this client to include as reconciliation lines
    const priorUnpaid = (store.invoices || [])
      .filter(i => i.clientId === client.id && i.status !== 'paid' && i.monthYear !== nowMonth())
      .sort((a, b) => (a.monthYear || '').localeCompare(b.monthYear || ''));

    const priorTotal    = priorUnpaid.reduce((s, i) => s + (i.amount || 0), 0);
    const cumulativeAmt = Number(amount) + priorTotal;

    const inv = {
      id: Date.now().toString(), clientId: client.id, monthYear: nowMonth(),
      invoiceNumber: getInvoiceNumber(client.name),
      amount: Number(amount), currency: client.currency || 'PKR', salesFigure, status: 'generated',
      generatedDate: today(), sentDate: null, clearedDate: null,
      dueDate: calcInvoiceDueDate(today(), client.billingDay).toISOString().slice(0,10),
      // Reconciliation data — prior unpaid invoices included in this invoice
      reconciliation: priorUnpaid.length > 0 ? {
        priorInvoices: priorUnpaid.map(i => ({
          invoiceNumber: i.invoiceNumber, monthYear: i.monthYear, amount: i.amount,
        })),
        priorTotal,
        cumulativeTotal: cumulativeAmt,
      } : null,
    };
    updated.invoices.push(inv);
    persist(updated);
    if (priorUnpaid.length > 0) {
      showToast(`Invoice ${inv.invoiceNumber} generated with ${priorUnpaid.length} prior outstanding invoice(s) — total ${fmtAmt(cumulativeAmt, client.currency)}.`);
    } else {
      showToast(`Invoice ${inv.invoiceNumber} generated.`);
    }
  }

  function confirmSalesGen() {
    const client = clients.find(c => c.id === pendingClientId);
    if (!client || !salesInput || Number(salesInput) <= 0) return;
    const amt = Math.round(Number(salesInput) * client.commissionRate / 100);
    doGenerate(client, amt, Number(salesInput));
    setShowSalesModal(false);
  }

  function generateAllDueToday() {
    // Skip any client that already has an invoice this month (no duplicates).
    const fixed   = dueToday.filter(c => c.billingType !== 'commission' && !invoiceExistsThisMonth(c.id));
    const skipped = dueToday.filter(c => c.billingType !== 'commission' && invoiceExistsThisMonth(c.id));
    const comm    = dueToday.filter(c => c.billingType === 'commission' && !invoiceExistsThisMonth(c.id));
    let updated = { ...store, invoices: [...(store.invoices || [])] };
    fixed.forEach(client => {
      updated.invoices.push({
        id: Date.now().toString() + Math.random(), clientId: client.id, monthYear: nowMonth(),
        invoiceNumber: getInvoiceNumber(client.name),
        amount: Number(client.amount), currency: client.currency || 'PKR', salesFigure: null, status: 'generated',
        generatedDate: today(), sentDate: null, clearedDate: null,
        dueDate: calcInvoiceDueDate(today(), client.billingDay).toISOString().slice(0,10),
      });
    });
    const skipNote = skipped.length ? ` ${skipped.length} skipped (already invoiced this month).` : '';
    if (fixed.length) { persist(updated); showToast(`${fixed.length} invoice(s) generated.${skipNote}`); }
    else if (skipped.length) showToast(`All due clients already have an invoice this month — nothing generated.`, 'info');
    if (comm.length) showToast(`${comm.map(c=>c.name).join(', ')} need manual entry (commission).`, 'info');
  }

  function updateStatus(invoiceId, status) {
    if (status === 'paid') celebrate('paid');             // gold coins
    else if (status === 'sent') celebrate('small');
    // Build updated invoice list first — cannot reference 'updated' inside map
    const updatedInvoices = store.invoices.map(inv => {
      if (inv.id !== invoiceId) return inv;
      const patch = { ...inv, status };
      if (status === 'sent') patch.sentDate = today();
      if (status === 'paid') patch.clearedDate = today();
      return patch;
    });

    const updated = { ...store, invoices: updatedInvoices };

    // Auto-record income when marking paid
    if (status === 'paid') {
      const inv    = store.invoices.find(i => i.id === invoiceId);
      const client = (store.clients || []).find(c => c.id === inv?.clientId);
      const revId  = 'inv-' + invoiceId;
      const exists = (store.oneTimeRevenue || []).find(r => r.id === revId);
      if (!exists && inv) {
        updated.oneTimeRevenue = [...(store.oneTimeRevenue || []), {
          id: revId,
          description: 'Invoice ' + (inv.invoiceNumber || invoiceId) + ' — ' + (client?.name || ''),
          amount: toPKR(inv.amount || 0, inv.currency || client?.currency || 'PKR', rates),
          origAmount: inv.amount || 0, origCurrency: inv.currency || client?.currency || 'PKR',
          date: today(),
          source: 'invoice',
          invoiceId,
        }];
      }
    }

    persist(updated);
    showToast('Status updated.');
  }

  const commCalc = pendingClientId && Number(salesInput) > 0
    ? Math.round(Number(salesInput) * (clients.find(c => c.id === pendingClientId)?.commissionRate || 0) / 100)
    : null;

  function outstandingForClient(clientId) {
    return invoices.filter(i => i.clientId === clientId && i.status !== 'paid')
      .reduce((s, i) => s + (i.amount || 0), 0);
  }

  // ── Record payment — clears oldest unpaid invoices first ─────────
  function recordPayment() {
    const client = allClients.find(c => c.id === payClientId);
    const amount = Number(payAmount);
    if (!client || !amount || amount <= 0) return;
    celebrate('paid');

    // Get all unpaid invoices for this client, oldest first
    const unpaid = (store.invoices || [])
      .filter(i => i.clientId === payClientId && i.status !== 'paid')
      .sort((a, b) => (a.monthYear || a.generatedDate || '').localeCompare(b.monthYear || b.generatedDate || ''));

    let remaining = amount;
    const nowPaidIds = new Set();

    for (const inv of unpaid) {
      if (remaining >= inv.amount) {
        remaining -= inv.amount;
        nowPaidIds.add(inv.id);
      } else {
        break; // Not enough to cover this invoice — stop
      }
    }

    // Update invoices
    const updatedInvoices = (store.invoices || []).map(inv => {
      if (!nowPaidIds.has(inv.id)) return inv;
      return { ...inv, status: 'paid', clearedDate: today(), paidViaPayment: true };
    });

    // Record revenue entries for each cleared invoice
    const newRevenue = [...(store.oneTimeRevenue || [])];
    for (const inv of unpaid.filter(i => nowPaidIds.has(i.id))) {
      const revId = 'inv-' + inv.id;
      if (!newRevenue.find(r => r.id === revId)) {
        newRevenue.push({
          id: revId,
          description: 'Invoice ' + (inv.invoiceNumber || inv.id) + ' — ' + client.name,
          amount: toPKR(inv.amount || 0, inv.currency || client.currency || 'PKR', rates),
          origAmount: inv.amount || 0, origCurrency: inv.currency || client.currency || 'PKR',
          date: today(),
          source: 'invoice',
          invoiceId: inv.id,
        });
      }
    }

    // Store credit balance if any leftover
    const updatedClients = (store.clients || []).map(cl =>
      cl.id === payClientId ? { ...cl, creditBalance: (cl.creditBalance || 0) + remaining } : cl
    );

    persist({ ...store, invoices: updatedInvoices, oneTimeRevenue: newRevenue, clients: updatedClients });

    const cleared = nowPaidIds.size;
    const msg = cleared > 0
      ? `Payment of ${fmtAmt(amount)} applied — ${cleared} invoice${cleared !== 1 ? 's' : ''} cleared.`
        + (remaining > 0 ? ` ${fmtAmt(remaining)} stored as credit.` : '')
      : `Payment recorded but PKR ${fmtAmt(amount)} is less than the oldest invoice (${fmtAmt(unpaid[0]?.amount || 0)}). Stored as credit.`;

    showToast(msg);
    setShowPayModal(false);
    setPayAmount('');
    setPayClientId(null);
  }

  const filterCounts = ['all','draft','generated','sent','pending','paid'].reduce((acc, f) => {
    acc[f] = f === 'all' ? invoices.length : invoices.filter(i => i.status === f).length;
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Page header */}
      <div>
        <h1 className="t-h1">Invoices</h1>
        <p className="t-sub" style={{ marginTop:4 }}>Manage client billing and track payment health across your portfolio.</p>
      </div>

      {/* Clients */}
      <Card>
        <SectionHeader
          title="Clients"
          count={clients.length + formerClients.length}
          action={
            <div style={{ display: 'flex', gap: 8 }}>
              {dueToday.length > 0 && (
                <Btn onClick={generateAllDueToday} variant="primary" size="sm">
                  <Zap size={12} /> {dueToday.length} due today
                </Btn>
              )}
              <Btn onClick={openAddClient} size="sm"><Plus size={12} /> Add client</Btn>
            </div>
          }
        />
        {clients.length === 0 ? <EmptyState message="No clients yet. Add your first client." /> : (
          <Table headers={['Client', 'Email', 'Type', 'Rate', 'Billing day', 'Outstanding', 'Actions']}>
            {clients.map(c => {
              const due = isDueTodayInMonth(c.billingDay) && !invoices.some(i => i.clientId === c.id && i.monthYear === nowMonth());
              return (
                <TR key={c.id} highlight={due}>
                  <TD bold>
                    <span style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 15.5, letterSpacing: '0.01em' }}>{c.name}</span>
                    {due && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: 'var(--accent)', background: 'rgba(169,137,83,0.12)', padding: '2px 7px', borderRadius: 'var(--radius)', fontFamily: 'var(--mono)' }}>due today</span>}
                    {c.parentClientId === 'tpc' && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--purple)', background: 'rgba(168,85,247,0.12)', padding: '2px 7px', borderRadius: 'var(--radius)', fontFamily: 'var(--mono)' }}>Parthenon</span>}
                    {(c.active === false && !c.formerClient) && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--text3)', background: 'var(--bg4)', padding: '2px 7px', borderRadius: 'var(--radius)', fontFamily: 'var(--mono)' }}>paused</span>}
                  </TD>
                  <TD muted>{c.email || '—'}</TD>
                  <TD><Badge label={c.billingType === 'commission' ? 'Commission' : 'Fixed'} color={c.billingType === 'commission' ? 'purple' : 'green'} /></TD>
                  <TD mono>{c.billingType === 'commission' ? `${c.commissionRate}% of sales` : fmtAmt(c.amount, c.currency)}</TD>
                  <TD muted style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{c.billingDay}{['st','nd','rd'][c.billingDay % 10 - 1] || 'th'}</TD>
                  <TD mono style={{ color: outstandingForClient(c.id) > 0 ? 'var(--orange)' : 'var(--text3)' }}>
                    {outstandingForClient(c.id) > 0 ? fmtAmt(outstandingForClient(c.id), c.currency) : '—'}
                  </TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Btn size="sm" onClick={() => generateInvoice(c.id)}>Generate</Btn>
                      {outstandingForClient(c.id) > 0 && (
                        <Btn size="sm" variant="success" onClick={() => { setPayClientId(c.id); setPayAmount(''); setShowPayModal(true); }}>
                          Record payment
                        </Btn>
                      )}
                      <Btn size="sm" variant="ghost" onClick={() => openEditClient(c)}>Edit</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => toggleClientPause(c.id, c.active)}>
                        {c.active === false ? 'Reactivate' : 'Pause'}
                      </Btn>
                      <Btn size="sm" variant="danger" onClick={() => deleteClient(c.id)}>Delete</Btn>
                    </div>
                  </TD>
                </TR>
              );
            })}
          </Table>
        )}
      </Card>

      {/* Former / Inactive clients with outstanding */}
      {formerClients.some(c => outstandingForClient(c.id) > 0) && (
        <Card>
          <SectionHeader title="Former clients — outstanding receivables"
            count={formerClients.filter(c => outstandingForClient(c.id) > 0).length}
          />
          <Table headers={['Client', 'Outstanding', 'Invoices', 'Actions']}>
            {formerClients.filter(c => outstandingForClient(c.id) > 0).map(c => {
              const outstanding = outstandingForClient(c.id);
              const unpaidInvs  = invoices.filter(i => i.clientId === c.id && i.status !== 'paid');
              return (
                <TR key={c.id}>
                  <TD bold>
                    <div style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 15.5, lineHeight: 1.3 }}>{c.name}</div>
                    <span style={{ display: 'inline-block', marginTop: 5 }}><Badge label="Former" color="gray" /></span>
                  </TD>
                  <TD mono style={{ color: 'var(--orange)', fontWeight: 700 }}>{fmtAmt(outstanding, c.currency)}</TD>
                  <TD muted>{unpaidInvs.length} unpaid</TD>
                  <TD>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Btn size="sm" variant="success" onClick={() => { setPayClientId(c.id); setPayAmount(''); setShowPayModal(true); }}>
                        Record payment
                      </Btn>
                      {unpaidInvs.length === 1 && (
                        <Btn size="sm" variant="ghost" onClick={() => updateStatus(unpaidInvs[0].id, 'paid')}>
                          Mark paid ({unpaidInvs[0].monthYear})
                        </Btn>
                      )}
                      {unpaidInvs.length > 1 && (
                        <Select value="" onChange={e => { if (e.target.value) updateStatus(e.target.value, 'paid'); }}
                          style={{ width: 'auto', padding: '7px 10px', fontSize: 12.5 }}>
                          <option value="">Mark paid…</option>
                          {unpaidInvs.map(inv => <option key={inv.id} value={inv.id}>{inv.monthYear}</option>)}
                        </Select>
                      )}
                    </div>
                  </TD>
                </TR>
              );
            })}
          </Table>
        </Card>
      )}

      {/* Aging report + outstanding summary */}
      <div className="grid-chart">
        <Card style={{ padding:'22px 24px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, flexWrap:'wrap', gap:8 }}>
            <span className="t-h3">Aging Report <span style={{ color:'var(--text3)', fontWeight:500, fontSize:15 }}>· overdue</span></span>
            <div style={{ display:'flex', gap:14, fontSize:12, fontWeight:600 }}>
              <span style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text3)' }}><span style={{width:9,height:9,borderRadius:'50%',background:'#8FB08A'}}/>30 days</span>
              <span style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text3)' }}><span style={{width:9,height:9,borderRadius:'50%',background:'#C79A5B'}}/>60 days</span>
              <span style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text3)' }}><span style={{width:9,height:9,borderRadius:'50%',background:'#C08079'}}/>90+ days</span>
            </div>
          </div>
          {(() => {
            const hasOverdue = (aging.buckets.d30 + aging.buckets.d60 + aging.buckets.d90) > 0;
            if (!hasOverdue) return (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, height:180, color:'var(--text3)', textAlign:'center' }}>
                <CheckCircle2 size={28} color="var(--green)"/>
                <div style={{ fontSize:14, fontWeight:600, color:'var(--text)' }}>Nothing overdue</div>
                <div style={{ fontSize:12.5 }}>All outstanding invoices are within terms.</div>
              </div>
            );
            const max = Math.max(aging.buckets.d30, aging.buckets.d60, aging.buckets.d90, 1);
            const bars = [['30 days', aging.buckets.d30, '#8FB08A'], ['60 days', aging.buckets.d60, '#C79A5B'], ['90+ days', aging.buckets.d90, '#C08079']];
            return (
              <div style={{ display:'flex', alignItems:'flex-end', gap:24, height:180, padding:'0 8px' }}>
                {bars.map(([lbl, val, col]) => (
                  <div key={lbl} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:8, height:'100%', justifyContent:'flex-end' }}>
                    <div className="t-currency" style={{ fontSize:13, color:'var(--text2)' }}>{val>0?showAmt(val):''}</div>
                    <div style={{ width:'100%', maxWidth:90, height:`${Math.max(val/max*130, val>0?8:2)}px`, background:col, borderRadius:'var(--radius) var(--radius) 0 0', transition:'height .5s cubic-bezier(.2,.8,.2,1)', opacity:val>0?1:0.25 }}/>
                    <div className="t-label">{lbl}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </Card>
        <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
          <Card style={{ padding:'20px 22px', borderTop:'3px solid var(--accent)' }}>
            <div className="t-label" style={{ marginBottom:8 }}>Total outstanding</div>
            <div className="t-currency fig-md" style={{ letterSpacing:'-0.02em', color:'var(--text)' }}>{showAmt(aging.totalOutstanding)}</div>
            <div style={{ fontSize:12.5, color:'var(--text3)', marginTop:5 }}>across all unpaid invoices{Object.keys(aging.byCurrency||{}).some(k=>k!=='PKR')?' · shown in PKR at live rate':''}</div>
            {Object.keys(aging.byCurrency||{}).length > 1 && <div style={{ fontSize:11.5, color:'var(--text3)', marginTop:6, display:'flex', gap:10, flexWrap:'wrap' }}>{Object.entries(aging.byCurrency).map(([cc,v]) => <span key={cc} className="t-currency">{fmtAmt(v, cc)}</span>)}</div>}
          </Card>
          <Card style={{ padding:'20px 22px', borderTop:'3px solid var(--red)' }}>
            <div className="t-label" style={{ marginBottom:8 }}>Overdue balance</div>
            <div className="t-currency fig-md" style={{ letterSpacing:'-0.02em', color:'var(--red)' }}>{showAmt(aging.overdue)}</div>
            <div style={{ fontSize:12.5, color:'var(--red)', marginTop:5, display:'flex', alignItems:'center', gap:5 }}><AlertCircle size={13}/> Action required</div>
          </Card>
        </div>
      </div>

      {/* Invoices */}
      <Card>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px', borderBottom:'1px solid var(--border)', flexWrap:'wrap', gap:10 }}>
          <span className="t-h3">Invoices <span style={{ fontFamily:'var(--mono)', fontVariantNumeric:'lining-nums tabular-nums', fontSize:12, fontWeight:600, color:'var(--text3)', background:'var(--bg3)', padding:'3px 9px', borderRadius:'var(--radius)', marginLeft:4 }}>{invoices.length}</span></span>
          <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
            <div className="segmented" style={{ padding:3 }}>
              {['all','generated','sent','pending','paid'].map(f => (
                <button key={f} onClick={() => setFilter(f)} className={filter===f?'active':''} style={{ padding:'6px 13px', fontSize:12.5 }}>
                  {f.charAt(0).toUpperCase()+f.slice(1)} <span style={{ opacity:0.55 }}>{filterCounts[f]}</span>
                </button>
              ))}
            </div>
            <div className="segmented" style={{ padding:3 }}>
              {['PKR','USD'].map(c => (
                <button key={c} onClick={()=>setCur(c)} className={cur===c?'active':''} style={{ padding:'6px 14px', fontSize:12.5 }}>{c}</button>
              ))}
            </div>
          </div>
        </div>
        {filteredInvoices.length === 0 ? <EmptyState message={invoices.length === 0 ? 'No invoices yet. Generate one from the clients above.' : 'No invoices match this filter.'} /> : (
          <Table headers={['Invoice #', 'Client', 'Amount', 'Status', 'Generated', 'Sent', 'Cleared', 'Actions']}>
            {filteredInvoices.map(inv => (
              <TR key={inv.id}>
                <TD mono bold>
                  {inv.invoiceNumber}
                  {inv.isHistorical && <span style={{marginLeft:6,fontSize:9,fontWeight:600,color:'var(--text3)',background:'var(--bg4)',padding:'1px 5px',borderRadius:'var(--radius)',textTransform:'uppercase',letterSpacing:'0.05em',fontFamily:'var(--mono)'}}>historical</span>}
                </TD>
                <TD bold>
                  {inv.client?.name || '—'}
                  {(() => {
                    const clientTotal = outstandingForClient(inv.clientId);
                    const clientUnpaid = invoices.filter(i => i.clientId===inv.clientId && i.status!=='paid').length;
                    return clientTotal > 0 && clientUnpaid > 1 ? (
                      <div style={{fontSize:10,color:'var(--orange)',fontWeight:500,marginTop:2}}>
                        {clientUnpaid} invoices outstanding · {fmtAmt(clientTotal, inv.currency || inv.client?.currency || 'PKR')} total
                      </div>
                    ) : null;
                  })()}
                </TD>
                <TD mono>{fmtAmt(inv.amount, inv.currency || inv.client?.currency || 'PKR')}</TD>
                <TD><StatusBadge status={inv.status} /></TD>
                <TD muted style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{fmtDate(inv.generatedDate)}</TD>
                <TD muted style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{fmtDate(inv.sentDate)}</TD>
                <TD style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', color: inv.clearedDate ? 'var(--green)' : undefined, fontWeight: inv.clearedDate ? 600 : 400 }}>{fmtDate(inv.clearedDate)}</TD>
                <TD>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    <Btn size="sm" variant="ghost" title="Open branded invoice (save as PDF)" onClick={() => { if (!openInvoiceDocument(inv.client || {}, inv)) { showToast('Allow pop-ups to open the invoice — downloading a copy instead.', 'info'); generateInvoicePDF(inv.client || {}, inv); } }}><Download size={11} /></Btn>
                    {inv.status === 'generated' && <Btn size="sm" onClick={() => updateStatus(inv.id, 'sent')}>Mark sent</Btn>}
                    {inv.status === 'sent' && <Btn size="sm" onClick={() => updateStatus(inv.id, 'pending')}>Pending</Btn>}
                    {(inv.status === 'sent' || inv.status === 'pending') && <Btn size="sm" variant="success" onClick={() => updateStatus(inv.id, 'paid')}>Mark paid</Btn>}
                    {inv.status !== 'paid' && <Btn size="sm" variant="danger" onClick={() => deleteInvoice(inv.id)}>Delete</Btn>}
                  </div>
                </TD>
              </TR>
            ))}
          </Table>
        )}
      </Card>

      {/* Add/Edit Client Modal */}
      <Modal open={showClientModal} onClose={() => setShowClientModal(false)} title={editingClient ? 'Edit client' : 'Add client'}>
        <Field label="Client name"><Input value={clientForm.name} onChange={e => setClientForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Acme Corp" autoFocus /></Field>
        <Field label="Client email"><Input type="email" value={clientForm.email} onChange={e => setClientForm(p => ({ ...p, email: e.target.value }))} placeholder="client@example.com" /></Field>
        <ModalRow>
          <Field label="Billing day of month"><Input type="number" min={1} max={28} value={clientForm.billingDay} onChange={e => setClientForm(p => ({ ...p, billingDay: e.target.value }))} placeholder="e.g. 5" /></Field>
          <Field label="Billing type">
            <Select value={clientForm.billingType} onChange={e => setClientForm(p => ({ ...p, billingType: e.target.value }))}>
              <option value="fixed">Fixed amount</option>
              <option value="commission">Commission %</option>
            </Select>
          </Field>
        </ModalRow>
        <Field label="Billing currency">
          <Select value={clientForm.currency || 'PKR'} onChange={e => setClientForm(p => ({ ...p, currency: e.target.value }))}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
        {clientForm.billingType === 'fixed' && (
          <Field label={`Monthly amount (${clientForm.currency || 'PKR'})`}><Input type="number" value={clientForm.amount} onChange={e => setClientForm(p => ({ ...p, amount: e.target.value }))} placeholder="e.g. 150000" /></Field>
        )}
        {clientForm.billingType === 'commission' && (
          <Field label="Commission rate (%)"><Input type="number" step="0.1" value={clientForm.commissionRate} onChange={e => setClientForm(p => ({ ...p, commissionRate: e.target.value }))} placeholder="e.g. 5" /></Field>
        )}

        {/* Report capabilities */}
        <div style={{ marginTop: '1rem', padding: '12px', background: 'var(--bg3)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 10, fontFamily: 'var(--mono)' }}>Report generation</div>
          <Field label="Performance marketing / Ad type">
            <Select value={clientForm.adType || ''} onChange={e => setClientForm(p => ({ ...p, adType: e.target.value || null }))}>
              <option value="">No ads / Not applicable</option>
              {CLIENT_AD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </Field>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Social media management</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Enable social media monthly reports for this client</div>
            </div>
            <button onClick={() => setClientForm(p => ({ ...p, socialMedia: !p.socialMedia }))} style={{ width: 44, height: 24, borderRadius: 12, border: '1px solid var(--border)', cursor: 'pointer', background: clientForm.socialMedia ? 'var(--accent)' : 'var(--bg4)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <div style={{ position: 'absolute', top: 3, left: clientForm.socialMedia ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: clientForm.socialMedia ? 'var(--bg)' : 'var(--text2)', transition: 'left 0.2s' }} />
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: '1.25rem' }}>
          <Btn onClick={() => setShowClientModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveClient}>Save client</Btn>
        </div>
      </Modal>

      {/* Sales Modal */}
      <Modal open={showSalesModal} onClose={() => setShowSalesModal(false)} title={`Generate invoice — ${clients.find(c => c.id === pendingClientId)?.name || ''}`}>
        <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: '1.25rem' }}>
          Commission rate: <strong style={{ color: 'var(--accent)' }}>{clients.find(c => c.id === pendingClientId)?.commissionRate}%</strong>. Enter this month's total sales.
        </p>
        <Field label={`Total sales this month (${clients.find(c => c.id === pendingClientId)?.currency || 'PKR'})`}><Input type="number" value={salesInput} onChange={e => setSalesInput(e.target.value)} placeholder="e.g. 2000000" autoFocus /></Field>
        {commCalc !== null && (
          <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '0.875rem 1rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 13, color: 'var(--text3)' }}>Invoice amount</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{fmtAmt(commCalc, clients.find(c => c.id === pendingClientId)?.currency)}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn onClick={() => setShowSalesModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={confirmSalesGen} disabled={!salesInput || Number(salesInput) <= 0}>Generate</Btn>
        </div>
      </Modal>
      {/* Record Payment Modal */}
      {showPayModal && (() => {
        const payClient = allClients.find(c => c.id === payClientId);
        const outstanding = outstandingForClient(payClientId);
        const unpaidInvs  = (store.invoices || [])
          .filter(i => i.clientId === payClientId && i.status !== 'paid')
          .sort((a,b) => (a.monthYear||'').localeCompare(b.monthYear||''));
        const existingCredit = payClient?.creditBalance || 0;
        const totalToApply   = (Number(payAmount) || 0) + existingCredit;

        // Preview: how many invoices will be cleared
        let preview = 0, previewRemaining = totalToApply;
        for (const inv of unpaidInvs) {
          if (previewRemaining >= inv.amount) { previewRemaining -= inv.amount; preview++; } else break;
        }

        return (
          <Modal open onClose={() => setShowPayModal(false)} title={`Record payment — ${payClient?.name}`}>
            {existingCredit > 0 && (
              <div style={{ background:'rgba(143,176,138,0.12)', border:'1px solid rgba(143,176,138,0.25)', borderRadius:'var(--radius)', padding:'10px 12px', fontSize:12, color:'#8FB08A', marginBottom:12 }}>
                Existing credit: <strong>{fmtAmt(existingCredit, payClient?.currency)}</strong> — will be added to this payment.
              </div>
            )}
            <div style={{ background:'var(--bg3)', borderRadius:'var(--radius)', padding:'10px 12px', fontSize:12, color:'var(--text3)', marginBottom:12 }}>
              <div style={{ marginBottom:4 }}>Total outstanding: <strong style={{color:'var(--orange)'}}>{fmtAmt(outstanding, payClient?.currency)}</strong></div>
              <div style={{ fontSize:11 }}>{unpaidInvs.length} unpaid invoice{unpaidInvs.length!==1?'s':''} — applied oldest first</div>
            </div>
            <Field label={`Amount received (${payClient?.currency || 'PKR'})`}>
              <Input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                placeholder="e.g. 200000" autoFocus />
            </Field>
            {payAmount && Number(payAmount) > 0 && (
              <div style={{ background:'var(--bg3)', borderRadius:'var(--radius)', padding:'10px 12px', fontSize:13, marginBottom:12 }}>
                <div style={{ marginBottom:4 }}>
                  Total to apply: <strong style={{color:'var(--accent)'}}>{fmtAmt(totalToApply, payClient?.currency)}</strong>
                  {existingCredit > 0 && <span style={{fontSize:11,color:'var(--text3)',marginLeft:6}}>(incl. {fmtAmt(existingCredit, payClient?.currency)} credit)</span>}
                </div>
                <div style={{ color:'var(--green)', marginBottom:2 }}>Will clear: <strong>{preview} invoice{preview!==1?'s':''}</strong></div>
                {previewRemaining > 0 && <div style={{color:'var(--blue)',fontSize:12}}>Credit remaining after: <strong>{fmtAmt(previewRemaining, payClient?.currency)}</strong></div>}
                {previewRemaining === 0 && preview > 0 && <div style={{color:'var(--text3)',fontSize:12}}>No credit remaining.</div>}
                {preview === 0 && totalToApply > 0 && <div style={{color:'var(--orange)',fontSize:12}}>Amount is less than oldest invoice ({fmtAmt(unpaidInvs[0]?.amount||0, payClient?.currency)}) — will be stored as credit.</div>}
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              <Btn onClick={() => setShowPayModal(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={recordPayment} disabled={!payAmount || Number(payAmount) <= 0}>
                Apply payment
              </Btn>
            </div>
          </Modal>
        );
      })()}

    </div>
  );
}
